import { Line, LineProps } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import {
  overlayMaterialDefines,
  RenderOrder,
} from 'features/assetViz/utils/renderingDefines'
import React from 'react'
import { Group, Vector3 } from 'three'

/**
 * Due to the way dashed Line objects are rendered, we need to un-apply any
 * scaling, then re-apply it to the individual points. Without this logic, we
 * end up with weirdly-scaled dashes, etc.
 * @param points            The coordinates of all points in the line
 * @param props             All properties of this line
 * @constructor
 */
export const ScaledLine3D: React.FC<
  {
    points: number[]
  } & Partial<LineProps>
> = ({ points, ...props }) => {
  //----------------------------------------------------------------------------

  const ref = React.useRef<Group>(null!)
  const vec3 = React.useRef(new Vector3())
  const [scaleX, setScaleX] = React.useState<number>(NaN)
  const [scaleY, setScaleY] = React.useState<number>(NaN)
  const [scaleZ, setScaleZ] = React.useState<number>(NaN)

  useFrame(() => {
    if (!ref.current) return
    ref.current.getWorldScale(vec3.current)
    setScaleX(vec3.current.x)
    setScaleY(vec3.current.y)
    setScaleZ(vec3.current.z)
  })

  //----------------------------------------------------------------------------

  const { inverseScale, scaledPoints } = React.useMemo(() => {
    if (Number.isNaN(scaleX + scaleY + scaleZ))
      return { inverseScale: undefined, scaledPoints: undefined }
    if (scaleX === 1 && scaleY === 1 && scaleZ === 1)
      return { inverseScale: undefined, scaledPoints: points }
    return {
      inverseScale: new Vector3(1.0 / scaleX, 1.0 / scaleY, 1.0 / scaleZ),
      // For every three coords, scale by x, y, and z respectively
      scaledPoints: points.map((coord, i) => {
        switch (i % 3) {
          case 0:
            return coord * scaleX
          case 1:
            return coord * scaleY
          default:
            return coord * scaleZ
        }
      }),
    }
  }, [points, scaleX, scaleY, scaleZ])

  //----------------------------------------------------------------------------

  return points.length === 0 ? null : (
    <group ref={ref}>
      {!scaledPoints ? null : (
        <Line
          segments
          renderOrder={RenderOrder.Markup}
          scale={inverseScale}
          points={scaledPoints}
          {...overlayMaterialDefines}
          {...props}
        />
      )}
    </group>
  )
}
